import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  Paper,
  Chip,
  Grid,
  Divider,
  Alert,
  Stack,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ArticleIcon from "@mui/icons-material/Article";
import { useState, useEffect } from "react";
import LoadingButton from "../../../components/LoadingButton";
import TagsAutocomplete from "../../../components/TagsAutocomplete";
import { Document } from "../../../types";

interface EditDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onSaveDocument: (document: Partial<Document>) => Promise<void>;
  document: Document | null;
  availableTags: string[];
  loading?: boolean;
  companyId: string;
  getToken: () => Promise<string>;
}

export default function EditDocumentDialog({
  open,
  onClose,
  onSaveDocument,
  document,
  availableTags,
  loading = false,
  companyId,
  getToken,
}: EditDocumentDialogProps) {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (document) {
      setName(document.name || "");
      setContent(document.content || "");
      setTags(document.tags || []);
    }
  }, [document]);

  // Update word and character count when content changes
  useEffect(() => {
    const words = content.trim() ? content.trim().split(/\s+/).length : 0;
    const chars = content.length;
    setWordCount(words);
    setCharCount(chars);
  }, [content]);

  const handleClose = () => {
    setName("");
    setContent("");
    setTags([]);
    setWordCount(0);
    setCharCount(0);
    onClose();
  };

  const handleSave = async () => {
    if (name.trim() && content.trim() && document) {
      await onSaveDocument({
        id: document.id,
        name: name.trim(),
        content: content.trim(),
        tags,
        type: document.type,
        companyId: document.companyId,
        path: document.path,
      });
      handleClose();
    }
  };

  const isValid = name.trim() && content.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      {loading && <LinearProgress />}
      
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Box>
            <Typography variant="h6" component="div">
              Edit Document
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {document?.name || "Document"}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={3}>
          {/* Left Column - Document Details */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <DescriptionIcon color="primary" />
                  <Typography variant="h6">Document Details</Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Document Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{ mb: 3 }}
                  autoFocus
                  disabled={loading}
                  required
                  error={!name.trim() && name !== ""}
                  helperText={!name.trim() && name !== "" ? "Document name is required" : ""}
                />

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocalOfferIcon color="primary" />
                    <Typography variant="subtitle2">Tags</Typography>
                  </Box>
                  <TagsAutocomplete
                    value={tags}
                    onChange={setTags}
                    companyId={companyId}
                    getToken={getToken}
                    placeholder="Add tags to categorize this document"
                    disabled={loading}
                  />
                </Box>
              </Paper>

              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ArticleIcon color="primary" />
                  <Typography variant="h6">Document Statistics</Typography>
                </Box>
                
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Word Count
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {wordCount.toLocaleString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Character Count
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {charCount.toLocaleString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Tags
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {tags.length}
                    </Typography>
                  </Box>
                </Stack>
                
                {!isValid && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please provide both a document name and content to save changes.
                  </Alert>
                )}
              </Paper>
            </Stack>
          </Grid>

          {/* Right Column - Content Editor */}
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={0} 
              sx={{ 
                height: '100%',
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid', 
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <ArticleIcon color="primary" />
                <Typography variant="h6">Content Editor</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {wordCount} words • {charCount} characters
                </Typography>
              </Box>
              
              <Box sx={{ 
                flexGrow: 1, 
                p: 2,
                height: '500px', // Set a fixed height for the container
                overflow: 'hidden' // Prevent container overflow
              }}>
                <TextField
                  fullWidth
                  multiline
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  disabled={loading}
                  required
                  placeholder="Start typing your document content here..."
                  variant="outlined"
                  sx={{
                    height: '100%',
                    '& .MuiOutlinedInput-root': {
                      height: '100%',
                      alignItems: 'flex-start',
                      '& fieldset': {
                        border: 'none'
                      },
                      '&:hover fieldset': {
                        border: 'none'
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      height: '100% !important',
                      overflow: 'auto !important', // Enable scrolling
                      resize: 'none' // Prevent manual resizing
                    }
                  }}
                  error={!content.trim() && content !== ""}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <Divider />
      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          Last saved: {document?.updated_date ? new Date(document.updated_date).toLocaleString() : 'Never'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button onClick={handleClose} disabled={loading} size="large">
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={loading}
            disabled={!isValid}
            size="large"
            startIcon={<EditIcon />}
          >
            Save Changes
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}